/**
 * Standalone Host Bridge Implementation
 *
 * Fallback implementation for when the app runs outside of a host environment
 * (e.g., direct browser access without iframe embedding).
 *
 * Provides mock user context and allows all ACL checks for development/testing.
 */

import { Injectable } from '@angular/core';
import {
  HostBridge,
  UserContext,
  AclCheckRequest,
  AclCheckResponse,
} from './host-bridge.interface';

@Injectable()
export class StandaloneHostBridge extends HostBridge {
  private messageHandlers = new Map<string, Set<(payload: any) => void>>();

  /**
   * Always returns false - not running in embedded mode
   */
  isEmbedded(): boolean {
    return false;
  }

  /**
   * Return a default development user for standalone mode
   */
  async getUserContext(): Promise<UserContext> {
    console.log('[StandaloneHostBridge] Returning default standalone user context');

    // Return a default user for development/testing
    return {
      sys_id: 'standalone_dev_user',
      user_name: 'dev.user',
      display_name: 'Development User',
      email: 'dev@rimini.com',
      avatar_url: undefined,
      roles: ['admin', 'user'],
      isImpersonating: false,
    };
  }

  /**
   * Allow all ACL checks in standalone mode
   */
  async checkAcl(request: AclCheckRequest): Promise<AclCheckResponse> {
    console.log('[StandaloneHostBridge] ACL check (allowing all in standalone mode):', request);

    // In standalone mode, allow all operations for development convenience
    return {
      allowed: true,
      reason: 'Standalone mode - all permissions granted',
    };
  }

  /**
   * Log messages in standalone mode (no actual sending)
   */
  sendMessage(type: string, payload: any): void {
    console.log('[StandaloneHostBridge] Message sent (no-op in standalone):', type, payload);
  }

  /**
   * Register a handler (kept for API compatibility)
   */
  onMessage<T>(type: string, handler: (payload: T) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type)!.add(handler);
    console.log('[StandaloneHostBridge] Message handler registered (no-op):', type);
  }

  /**
   * Remove a message handler
   */
  offMessage<T>(type: string, handler: (payload: T) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(type);
      }
    }
  }

  /**
   * Clean up (minimal work needed for standalone)
   */
  destroy(): void {
    this.messageHandlers.clear();
    console.log('[StandaloneHostBridge] Destroyed');
  }

  /**
   * Simulate an incoming message (useful for testing)
   */
  simulateIncomingMessage<T>(type: string, payload: T): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`[StandaloneHostBridge] Error in simulated message handler:`, error);
        }
      });
    }
  }
}
